<template>
  <client-only>
    <div :id="data.id" class="bg-gray-lighten3 pb-10 pt-14 text-slate">
      <div class="container overflow-hidden">
        <UiSectionTitle
          v-show="data"
          :title="data.title"
          class="title mb-6 text-3xl uppercase leading-none text-slate md:text-center md:text-5xl lg:text-[3.5rem]"
        />

        <div
          v-show="data && data.text"
          class="mb-8 max-w-full text-md leading-[1.8rem] md:mb-12 md:text-center xl:mx-auto xl:w-[685px]"
          v-html="data.text"
        />

        <div v-show="data && data.slides">
          <div class="-mx-6 overflow-auto scroll-smooth px-6">
            <div
              ref="sliderNav"
              class="mb-4 flex w-max min-w-full justify-center gap-x-4 md:justify-between"
            >
              <div
                v-for="(slide, i) in data.slides"
                :key="i"
                class="title flex h-14 min-w-[140px] cursor-pointer items-center justify-center rounded-lg px-4 text-sm uppercase tracking-[.12em] transition-all md:flex-grow lg:h-[4rem]"
                :class="
                  activeSlide === i
                    ? 'bg-blue text-white'
                    : 'bg-white text-slate'
                "
                @click="changeSlide(i)"
                v-html="slide.name"
              />
            </div>
          </div>

          <SolutionsCarouselSlider
            v-if="isDesktop"
            :slides="data.slides"
            @init="onSwiperInit($event)"
            @slide-change="onSlideChange"
            @play-video="playVideo"
          ></SolutionsCarouselSlider>

          <SolutionsCarouselSliderMobile
            v-else
            :slides="data.slides"
            @init="onSwiperMobileInit($event)"
            @slide-change="onSlideChange"
          ></SolutionsCarouselSliderMobile>
        </div>
      </div>

      <VideoPlayerModal
        :id="videoInModal"
        ref="videoPlayer"
        v-model="isModalOpen"
        @close="closeVideo"
      ></VideoPlayerModal>
    </div>
  </client-only>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useBreakpoint, useModalDialog } from '~/composables';
import SolutionsCarouselSlider from '~/components/sections/sliderSection/SolutionsCarouselSlider.vue';
import SolutionsCarouselSliderMobile from '~/components/sections/sliderSection/SolutionsCarouselSliderMobile.vue';
import VideoPlayerModal from '~/components/VideoPlayerModal.vue';

interface Props {
  data?: any;
}

withDefaults(defineProps<Props>(), {
  data: () => ({}),
});

const { isBiggerThanBreakpoint: isDesktop } = useBreakpoint('md');

const sliderNav = ref();
const activeSlide = ref(0);
const videoPlayer = ref();
const videoInModal = ref<undefined | string>();
const swiper = ref<any>();
const swiperMobile = ref<any>();
const { isOpen: isModalOpen, open, close } = useModalDialog('video-player');

const playVideo = (videoId: string) => {
  videoInModal.value = videoId;
  open();
};

const closeVideo = () => {
  videoInModal.value = undefined;
  close();
};

const onSlideChange = (i: number) => {
  activeSlide.value = i;

  const nav = sliderNav.value as HTMLElement;
  const navButton = nav.children[i] as HTMLElement;

  nav.parentElement!.scroll({
    left: navButton.offsetLeft - navButton.offsetWidth,
  });
};

const changeSlide = (i: number) => {
  if (swiper.value && !swiper.value.destroyed) swiper.value.slideTo(i);

  if (swiperMobile.value && !swiperMobile.value.destroyed)
    swiperMobile.value.slideTo(i);
};

const onSwiperInit = (slider: any) => {
  swiper.value = slider;
};

const onSwiperMobileInit = (slider: any) => {
  swiperMobile.value = slider;
};
</script>
